import React, { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { MessageSquare, MapPin, Sparkles, Globe, Moon, Sun, Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { cn } from '../lib/utils';

const languageNames = {
  tr: 'Türkçe',
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français'
};

export const PublicHeader = ({ title, description, children, projectName, tenantSlug: propTenantSlug }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { tenantSlug: paramTenantSlug } = useParams();
  const { t, language, setLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();

  // Get tenantSlug from props, params, or extract from pathname
  const currentTenantSlug = propTenantSlug || paramTenantSlug || location.pathname.split('/').filter(Boolean)[1];
  
  const navItems = [
    { 
      path: `/feedback/${currentTenantSlug}`, 
      label: t.feedbackNav, 
      icon: MessageSquare 
    },
    { 
      path: `/roadmap/${currentTenantSlug}`, 
      label: t.roadmapNav, 
      icon: MapPin 
    },
    { 
      path: `/changelog/${currentTenantSlug}`, 
      label: t.changelogNav, 
      icon: Sparkles 
    },
  ];

  const isActive = (path) => {
    if (path.includes('/feedback/')) {
      return location.pathname.includes('/feedback/');
    }
    return location.pathname === path;
  };

  return (
    <header className="bg-white dark:bg-gray-900 border-b dark:border-gray-800 sticky top-0 z-50 shadow-sm dark:shadow-gray-950/50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6">
        {/* Top Bar with Logo */}
        <div className="py-3 sm:py-4 border-b dark:border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              <img
                src="/logo-256.webp"
                alt="FeedPanels Logo"
                className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 object-contain"
              />
              <div className="hidden sm:block">
                <p className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white" aria-label="FeedPanels">FeedPanels</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t.publicPortal}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
              {/* Theme Toggle Button */}
              <button
                onClick={toggleTheme}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0"
                aria-label="Toggle theme"
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? (
                  <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" />
                ) : (
                  <Moon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                )}
              </button>

              {/* Language Selector - Mobile Dropdown */}
              <div className="sm:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0"
                      aria-label="Select language"
                    >
                      <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40 dark:bg-gray-800 dark:border-gray-700">
                    {Object.entries(languageNames).map(([code, name]) => (
                      <DropdownMenuItem
                        key={code}
                        onClick={() => setLanguage(code)}
                        className={`cursor-pointer dark:hover:bg-gray-700 ${
                          language === code ? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400' : ''
                        }`}
                      >
                        <span>{name}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Language Selector - Desktop */}
              <div className="hidden sm:block">
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="w-auto h-8 sm:h-9 text-xs sm:text-sm dark:bg-gray-800 dark:border-gray-700">
                    <Globe className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-gray-800 dark:border-gray-700">
                    <SelectItem value="tr">{languageNames.tr}</SelectItem>
                    <SelectItem value="en">{languageNames.en}</SelectItem>
                    <SelectItem value="de">{languageNames.de}</SelectItem>
                    <SelectItem value="es">{languageNames.es}</SelectItem>
                    <SelectItem value="fr">{languageNames.fr}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Project Name - Hidden on small screens */}
              {projectName && (
                <div className="hidden sm:block px-3 py-1.5 sm:px-4 sm:py-2 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg flex-shrink-0">
                  <span className="text-xs sm:text-sm font-semibold text-cyan-700 dark:text-cyan-400">{projectName}</span>
                </div>
              )}

              {/* Mobile Menu Button - Visible only on small screens */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <button className="sm:hidden flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all flex-shrink-0">
                    {mobileMenuOpen ? (
                      <X className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                    ) : (
                      <Menu className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                    )}
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="dark:bg-gray-900 dark:border-gray-700 w-64">
                  <div className="flex flex-col space-y-2 mt-8">
                    {navItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={cn(
                          'flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors font-medium text-sm',
                          isActive(item.path)
                            ? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                        )}
                      >
                        <item.icon className="w-4 h-4" />
                        <span>{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        {/* Navigation - Desktop only */}
        <nav className="hidden sm:flex items-center space-x-1 py-3 overflow-x-auto">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-colors font-medium text-xs sm:text-sm whitespace-nowrap',
                isActive(item.path)
                  ? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              )}
              data-testid={`public-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Page Header */}
        {title && (
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">{title}</h1>
                {description && <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">{description}</p>}
              </div>
              {children && <div className="w-full sm:w-auto">{children}</div>}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};